import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import { selectedListItem, selectItem } from '@/services/keymapService/keymapService'
import { isCtrlEvent, isCtrlOrShiftEvent, isShiftEvent } from '@/utils/kbEvents'

/**
 * This is the mixin for Bulk actions.It listens to DOM events and selects/unselects corresponding rows.
 */
export default {
  mixins: [EventBusMixin],
  computed: {
    selectedItems() {
      return this.$store.getters['bulkAction/selectedItems']
    },
    isSelectionLocked() {
      return this.$store.getters['bulkAction/isSelectionLocked']
    }
  },
  methods: {
    getInitiativeDOM(element) {
      if (!element) return null
      else if (element.dataset.initiativeId) {
        return element
      } else {
        return this.getInitiativeDOM(element.parentElement)
      }
    },
    getInitiativeDOMById(initiativeId) {
      return document.querySelector(
        `[data-initiative-row=true][data-initiative-id="${initiativeId}"]`
      )
    },
    isClickedOnNonDismissibleItem(element) {
      // clicking on tray should not dismiss multi-selection
      if (!element) return false
      else if (element.dataset.preventMultiselectDimissal) {
        return true
      } else {
        return this.isClickedOnNonDismissibleItem(element.parentElement)
      }
    },
    dismissBulkSelection() {
      const lastInitiative = this.selectedItems[this.selectedItems.length - 1]
      this.$store.commit('bulkAction/DISMISS')
      if (lastInitiative) {
        setTimeout(() => {
          const selectedItem = selectedListItem(this.$el)
          if (!selectedItem) {
            selectItem(this.$el.querySelector(`[data-initiative-id="${lastInitiative}"]`))
          }
        }, 200)
      }
    },
    getSelectedInitiative() {
      return document.querySelector('[data-initiative-row=true].select-list-item.selected')
    },
    getAncestors(node) {
      if (node.tagName !== 'BODY') return [node].concat(this.getAncestors(node.parentElement))
      else return [node]
    },
    findFirstCommonAncestor(nodeA, nodeB, ancestorsB) {
      const _ancestorsB = ancestorsB || this.getAncestors(nodeB)
      if (_ancestorsB.length === 0) return null
      else if (_ancestorsB.includes(nodeA)) return nodeA
      else if (nodeA.tagName === 'BODY') return null
      else return this.findFirstCommonAncestor(nodeA.parentElement, nodeB, _ancestorsB)
    },
    selectInitiativesTill(currentInitiative) {
      let lastSelectedInitiativeId
      if (this.selectedItems.length > 0) {
        lastSelectedInitiativeId = this.selectedItems[this.selectedItems.length - 1]
      } else {
        const selectedInitiative = this.getSelectedInitiative()
        if (selectedInitiative) {
          lastSelectedInitiativeId = +selectedInitiative.dataset.initiativeId
        }
      }
      const lastSelectedInitiativeDOM = this.getInitiativeDOMById(lastSelectedInitiativeId)
      if (lastSelectedInitiativeDOM) {
        const commonElement = this.findFirstCommonAncestor(
          currentInitiative,
          lastSelectedInitiativeDOM
        )
        let initiatives = commonElement.dataset.initiativeRow ? [commonElement] : []
        const children = Array.from(commonElement.querySelectorAll('[data-initiative-row=true]'))
        initiatives = [...initiatives, ...children]
        const range = [currentInitiative, lastSelectedInitiativeDOM]
          .map(elem => initiatives.indexOf(elem))
          .sort((a, b) => a - b)
        const rangeInitiativeIds = initiatives
          .slice(range[0], range[1] + 1)
          .map(initiative => +initiative.dataset.initiativeId)
        this.$store.commit('bulkAction/ADD_ITEMS', rangeInitiativeIds)
      }
    },
    unselectNonInitiativeItems() {
      const nonInitiativeSelectedElements = this.$el.querySelectorAll(
        '.select-list-item.selected:not([data-initiative-row=true])'
      )
      nonInitiativeSelectedElements.forEach(element => {
        element.classList.remove('selected')
      })
    },
    onItemClick(event) {
      const initiativeElement = this.getInitiativeDOM(event.target)
      if (
        (!initiativeElement || !isCtrlOrShiftEvent(event)) &&
        !this.isClickedOnNonDismissibleItem(event.target) &&
        !this.isSelectionLocked
      ) {
        // exit multi-selection
        this.dismissBulkSelection()
      } else if (isCtrlEvent(event)) {
        const selectedItems = new Set()
        if (this.selectedItems.length === 0) {
          const selectedInitiative = this.getSelectedInitiative()
          if (selectedInitiative) {
            selectedItems.add(+selectedInitiative.dataset.initiativeId)
          }
        }
        selectedItems.add(+initiativeElement.dataset.initiativeId)
        this.unselectNonInitiativeItems()
        if (selectedItems.size === 1) {
          this.$store.commit('bulkAction/TOGGLE_ITEM', [...selectedItems][0])
        } else {
          this.$store.commit('bulkAction/SET_ITEMS', [...selectedItems])
        }
      } else if (isShiftEvent(event)) {
        this.selectInitiativesTill(initiativeElement)
      }
    },
    onPointerDown(event) {
      const initiativeElement = this.getInitiativeDOM(event.target)
      if (initiativeElement && isShiftEvent(event)) {
        event.preventDefault() // prevent selection of text
      }
    },
    onKeyDown(event) {
      if (!this.isSelectionLocked && !isCtrlOrShiftEvent(event)) {
        this.dismissBulkSelection()
      }
    }
  },
  mounted() {
    window.addEventListener('mouseup', this.onItemClick, { capture: true })
    window.addEventListener('mousedown', this.onPointerDown, { capture: true })
    document.addEventListener('keydown', this.onKeyDown)
    this.listenToEventBus(events.BULK_ACTION_DONE, () => {
      this.$store.commit('bulkAction/DISMISS')
    })
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onItemClick, { capture: true })
    window.removeEventListener('mousedown', this.onPointerDown, { capture: true })
    document.removeEventListener('keydown', this.onKeyDown)
  }
}
